import type { EmailTheme } from '../email-theme';
import { getEmailTheme } from '../email-theme';

/**
 * Base email template with consistent styling.
 * Theme is read from a single place (email-theme.ts); pass theme to override (e.g. per-tenant later).
 *
 * Consumers (all wrap body HTML via `getBaseEmailTemplate` — test when changing layout/spacing):
 * - room-share-template.ts, room-share-reminder-template.ts (invites)
 * - share-content-template.ts
 * - broadcast-template.ts, play-message-template.ts
 * - team-invite-template.ts
 * - chat-notification-template.ts, activity-notification-template.ts, visit-notification-template.ts
 * - daily-summary-template.ts, bounce-alert-template.ts
 */
export function getBaseEmailTemplate(
  content: string,
  footerMessage?: string,
  optOutLink?: string,
  emailType?: string,
  roomId?: string,
  preheaderText?: string,
  theme?: EmailTheme
): string {
  const t = theme ?? getEmailTheme();

  // Helper function to get email type label
  const getEmailTypeLabel = (type?: string): string => {
    if (!type) return 'emails';
    const labels: Record<string, string> = {
      NOTIFICATION: 'notifications',
      BROADCAST: 'broadcasts',
      DAILY_SUMMARY: 'daily summaries',
      CHAT_MESSAGE: 'chat messages',
      ROOM_SHARE: 'room share emails',
      SHARE_CONTENT: 'content share emails',
      SCHEDULED: 'scheduled emails',
      PLAY_MESSAGE: 'play messages',
      PASSWORD_RESET: 'password reset emails',
      TEAM_INVITE: 'team invite emails',
      EMAIL_VERIFICATION: 'email confirmation',
    };
    return labels[type] || 'emails';
  };

  // Generate opt-out section if link is provided
  const optOutSection = optOutLink ? `
    <p style="margin-top: 16px; font-size: 11px; color: #9ca3af; line-height: 1.5;">
      <a href="${optOutLink}" style="color: #6b7280; text-decoration: underline;">
        Unsubscribe from ${getEmailTypeLabel(emailType)}${roomId ? ' for this room' : ''}
      </a>
    </p>
  ` : '';
  return `
<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <!--[if mso]>
  <xml>
    <o:OfficeDocumentSettings>
      <o:AllowPNG/>
      <o:PixelsPerInch>96</o:PixelsPerInch>
    </o:OfficeDocumentSettings>
  </xml>
  <![endif]-->
  <title>Smart Rooms</title>
  <style>
    /* Reset styles */
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: ${t.bodyText};
      background-color: ${t.pageBackground};
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }
    
    table {
      border-collapse: collapse;
      mso-table-lspace: 0pt;
      mso-table-rspace: 0pt;
    }
    
    img {
      border: 0;
      height: auto;
      line-height: 100%;
      outline: none;
      text-decoration: none;
      -ms-interpolation-mode: bicubic;
    }
    
    .email-container {
      max-width: 600px;
      margin: 0 auto;
      background-color: ${t.contentBackground};
    }
    
    .header {
      background: ${t.headerBackground};
      background-color: ${t.headerBackground};
      padding: 26px 18px 22px;
      text-align: center;
    }
    
    .header h1 {
      margin: 0;
      font-size: 26px;
      font-weight: 600;
      letter-spacing: -0.5px;
      color: ${t.headerTextColor};
    }
    
    .content {
      padding: 28px 22px 30px;
      background-color: ${t.contentBackground};
    }
    
    .content p {
      margin: 0 0 16px 0;
      font-size: 16px;
      color: ${t.bodyText};
    }
    
    .content p:last-child {
      margin-bottom: 0;
    }
    
    .highlight-box {
      background-color: #f8f9fa;
      border-left: 4px solid ${t.accentBorder};
      padding: 20px;
      margin: 24px 0;
      border-radius: 4px;
    }
    
    .highlight-box strong {
      color: ${t.primary};
      font-weight: 600;
    }
    
    .info-row {
      display: flex;
      padding: 12px 0;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .info-row:last-child {
      border-bottom: none;
    }
    
    .info-label {
      font-weight: 600;
      color: ${t.mutedText};
      width: 120px;
      flex-shrink: 0;
    }
    
    .info-value {
      color: #111827;
      flex: 1;
    }
    
    .button {
      display: inline-block;
      padding: 12px 24px;
      background-color: ${t.primary};
      color: #ffffff;
      text-decoration: none;
      border-radius: 6px;
      font-weight: 500;
      margin: 20px 0;
    }
    
    .button:hover {
      background-color: ${t.primaryHover};
    }
    
    .footer {
      background-color: ${t.footerBackground};
      padding: 22px 22px 24px;
      text-align: center;
      border-top: 1px solid #e5e7eb;
    }
    
    .footer p {
      margin: 0;
      font-size: 13px;
      color: ${t.mutedText};
      line-height: 1.5;
    }
    
    .footer-logo {
      color: ${t.footerLink};
      font-weight: 600;
      font-size: 16px;
      margin-bottom: 8px;
    }
    
    /* Mobile responsive styles */
    @media only screen and (max-width: 600px) {
      .email-container {
        width: 100% !important;
      }
      
      .content {
        padding: 24px 18px 26px !important;
      }
      
      .header {
        padding: 22px 16px 18px !important;
      }
      
      .header h1 {
        font-size: 22px !important;
      }
      
      .info-row {
        flex-direction: column;
      }
      
      .info-label {
        width: 100%;
        margin-bottom: 4px;
      }
      
      /* Make tables stack on mobile */
      table[class="responsive-table"] {
        width: 100% !important;
      }
      
      /* Adjust padding for mobile */
      td[class="mobile-padding"] {
        padding: 20px !important;
      }
      
      /* Make text smaller on mobile if needed */
      .mobile-text-small {
        font-size: 14px !important;
      }
    }
    
    /* Outlook-specific fixes */
    @media screen and (max-width: 525px) {
      .email-container {
        width: 100% !important;
      }
    }
  </style>
  <!--[if mso]>
  <style type="text/css">
    .header {
      background: ${t.headerBackground} !important;
    }
    .header h1 { color: ${t.headerTextColor} !important; }
    table {
      border-collapse: collapse;
    }
  </style>
  <![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: ${t.pageBackground};">
  <!-- Preheader text (hidden but shows in email preview) -->
  <div style="display: none; max-height: 0; overflow: hidden; mso-hide: all;">
    ${preheaderText ?? 'Notification from Smart Rooms'}
  </div>
  
  <!-- 100% width table for email client compatibility -->
  <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="100%" style="background-color: ${t.pageBackground};">
    <tr>
      <td align="center" style="padding: 16px 12px;">
        <!-- Main email container -->
        <table role="presentation" border="0" cellpadding="0" cellspacing="0" width="600" class="email-container" style="max-width: 600px; background-color: ${t.contentBackground};">
          <tr>
            <td>
              ${content}
              <div class="footer">
                <div class="footer-logo" style="margin-bottom: 12px;">
                  <a href="https://www.smartroomsxp.com" target="_blank" style="color: ${t.footerLink}; text-decoration: none; font-weight: 600; font-size: 16px;">Smart Rooms XP</a>
                </div>
                <p style="margin: 0 0 8px 0;">This is an automated notification from Smart Rooms XP.</p>
                <p style="margin: 0 0 12px 0; font-size: 12px;">${footerMessage || 'You are receiving this email because you are the owner of this room.'}</p>
                <p style="margin: 0; font-size: 11px; color: #9ca3af;">
                  <a href="https://www.smartroomsxp.com/terms" target="_blank" style="color: #6b7280; text-decoration: underline; margin-right: 12px;">Terms of Use</a>
                  <a href="https://www.smartroomsxp.com/privacy" target="_blank" style="color: #6b7280; text-decoration: underline;">Privacy Policy</a>
                </p>
                ${optOutSection}
              </div>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
  `.trim();
}

